<script setup>
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';

import { DEV_MODE } from '@/config/dev-mode';
import { fetchBaseServiceAPI } from '@/api/baseServiceApi';
import { useGesture } from '@/composables/calendar/useGesture';
import { useDeviceDetect } from '@/composables/useDeviceDetect';
import { useAnalysisStore } from '@/store/modules/analysis';

import SummarySub01 from '@/pages/mobile/saju-disposition/_summary_sub_01.vue';
import SummarySub02 from '@/pages/mobile/saju-disposition/_summary_sub_02.vue';
import ConfirmComponent from '@/components/common/Confirm.vue';
import IconAnalysNav210 from '@/components/svg/IconAnalysNav210.vue';
import '@/assets/nangman_style/popup/saju-reading_mobile.scss';

const analysisStore = useAnalysisStore();

definePage({ meta: { layout: 'empty' } });
const emit = defineEmits(['close', 'handleRegSave', 'redirectToLogin']);
const props = defineProps({
  prSajuData: Object,
  prBirthData: Object,
  langMode: String,
  // five: Object,
  transData: Object,
  rsGuestUserId: Number,
});

const handleRegSave = (callback) => {
  emit('handleRegSave', callback);
};

const redirectToLogin = () => {
  emit('redirectToLogin', { redirectOption: 'personality' });
};

/** 디바이스 감지 */
const { isMobile } = useDeviceDetect();
const route = useRoute();
const router = useRouter();
const currentPage = ref(0);
const infoPage = ref(false);
const dialogConfirm = ref(null);
const dialogTitle = ref('');
const dialogTitle2 = ref('');
const dialogText = ref('');
const dialogNextUrl = ref('');
const pData = ref({});
const pSubData = ref({});

const guestUserId = ref(props.rsGuestUserId || null);

/** 사주풀이 데이터 */
const sData = ref({});
const birthData = ref(props.prBirthData || null);
const baseCode = ref('');
// baseCode 생성하기
const codeValues = computed(() => {
  if (!birthData.value) return null;
  return [
    birthData?.value.sky.hour?.value !== '-' ? birthData?.value.sky.hour.value : '-',
    birthData?.value.earth.hour?.value !== '-' ? birthData?.value.earth.hour.value : '-',
    birthData?.value.sky.day?.value,
    birthData?.value.earth.day?.value,
    birthData?.value.sky.month?.value,
    birthData?.value.earth.month?.value,
    birthData?.value.sky.year?.value,
    birthData?.value.earth.year?.value,
  ];
});

/** 사주풀이 요청 데이터 */
const params = computed(() => {
  if (!props.prSajuData || Object.keys(props.prSajuData).length === 0) {
    return null;
  }

  const sajuData = props.prSajuData;

  // baseCode 생성하기
  baseCode.value = codeValues.value.join('');
  const paramsObject = {
    guestUserId: guestUserId.value ?? null,
    baseCode: baseCode.value,
    lang: 'KOR',
    birth: sajuData.solar ?? null,
    userName: sajuData.inputdata.name ?? '', // TODO: '' 대신 analysisStore 활용
    bln: sajuData.transdata.lucknum ?? null,
    gender: Number(sajuData.inputdata.gender) ?? null,
    unknownTime: sajuData.inputdata.unknown_time ?? null,
    analysisTypeId: 8,
  };

  return paramsObject;
});

const store = useStore();
const userId = ref(null);
userId.value = store.getters['user/id'];
const accessToken = ref(!!userId.value || !!sessionStorage.getItem('X-Token'));

/** 분석권 정보 요청중 */
const loadingTicketInfo = ref(false);

onMounted(async () => {
  readyToShow.value = true;

  if (props.rsGuestUserId) {
    guestUserId.value = props.rsGuestUserId;
    loadingTicketInfo.value = true;
    try {
      await analysisStore.getAnalysisLog(props.rsGuestUserId, 3); // 성향분석 typeId: 3
    } catch (e) {
    } finally {
      loadingTicketInfo.value = false;
    }
  }
});

const isFetchingData = ref(false);
/** 성향분석 데이터 요청 */
const fetchSajuDescData = async () => {
  if (!params.value || isFetchingData.value) return;

  isFetchingData.value = true;

  try {
    const result = await fetchBaseServiceAPI(params.value);

    if (result?.data?.message) {
      sData.value = {};
      pSubData.value = {};

      resetPopup();
      readyToShow.value = true;
      isFetchingData.value = false;
    } else {
      sData.value = result?.data;
      pSubData.value = result?.data?.성향종합분석;
    }
  } catch (error) {
    DEV_MODE && console.error('fetchSajuDescData 실패', error);
    resetPopup();
    readyToShow.value = true;
  } finally {
    isFetchingData.value = false;
  }
};

watch(
  () => params.value,
  async (newParams) => {
    if (newParams) await fetchSajuDescData();
  },
  { immediate: true },
);

/** 성향분석권 활성화 여부 */
const personalityPermission = computed(() => {
  if (!guestUserId.value) return false;
  const logList =
    analysisStore.analysisLogByType[3] ||
    analysisStore.analysisLogData?.filter((item) => item.analysisTypeId === 3) ||
    [];
  return logList.length > 0;
});
watch(guestUserId, async (newId) => {
  if (newId) {
    loadingTicketInfo.value = true;
    try {
      await analysisStore.getAnalysisLog(newId, 3); // 성향분석 typeId: 3
    } catch (e) {
    } finally {
      loadingTicketInfo.value = false;
    }
  }
});

const readyToShow = ref(false);

const clickPrevPage = () => {
  if (currentPage.value < 1) return;
  else currentPage.value -= 1;
};

const clickNextPage = () => {
  if (currentPage.value > 6) return;
  else currentPage.value += 1;
};

const onSwipeLeft = () => {
  if (currentPage.value === 0 || currentPage.value > 6) return;
  else currentPage.value += 1;
};

const onSwipeRight = () => {
  if (currentPage.value < 1) return;
  else currentPage.value -= 1;
};

const openInfoPage = () => {
  if (loadingTicketInfo.value || isFetchingData.value) return;

  const sDataLength = Object.keys(sData.value || {}).length;
  const pSubDataLength = Object.keys(pSubData.value || {}).length;
  if (sDataLength === 0 && pSubDataLength === 0) {
    return;
  }

  if (personalityPermission.value && route.params.id) {
    analysisStore.selectedMenu = '/overviewTendency';
    router.push(`/analysis/${route.params.id}`);
  } else {
    currentPage.value = 1;
    infoPage.value = false;
  }
};

/** 통합 제스처 핸들러 */
const { handleStart, handleMove, handleEnd } = useGesture({
  onSwipeLeft: onSwipeLeft, // 왼쪽 스와이프 → 다음 페이지 이동
  onSwipeRight: onSwipeRight, // 오른쪽 스와이프 → 이전 페이지 이동
  SWIPE_THRESHOLD: 150,
});

/** 팝업 닫기 */
const close = () => {
  emit('close');

  setTimeout(() => {
    readyToShow.value = false;
  }, 500);
};

const resetPopup = () => {
  infoPage.value = false;
  currentPage.value = 0;
};

const routeTab = () => {
  if (accessToken.value) {
    analysisStore.selectedMenu = '/overviewTendency';

    if (route.params.id) {
      router.push(`/analysis/${route.params.id}`);
    } else {
      handleRegSave(() => router.push('/saju-service'));
    }
  } else {
    showDialogConfirm('로그인 후 이용해 주세요.', '', '로그인', () => {
      redirectToLogin();
    });
  }
};

const goToPurchasePage = () => {
  router.push(
    DEV_MODE ? '/purchase/ticket-detail/2521/7148' : '/purchase/ticket-detail/2521/9204',
  );
};

const showDialogConfirm = (title, title2 = '', text = '', onConfirm = () => {}) => {
  dialogTitle.value = title;
  dialogTitle2.value = title2;
  dialogText.value = text;
  dialogNextUrl.value = onConfirm; // 함수로 전달

  dialogConfirm.value.openConfirmDialog();
};
</script>

<template>
  <div
    class="pop-saju-reading-container st-mobile-app"
    :class="{ mobile: isMobile }"
    @mousedown="handleStart"
    @mousemove="handleMove"
    @mouseup="handleEnd"
    @touchstart="handleStart"
    @touchmove="handleMove"
    @touchend="handleEnd"
  >
    <div
      v-if="currentPage === 0 && readyToShow"
      class="pop-saju-reading-inner intro st-disposition"
    >
      <div class="pop-header__large intro">
        <button @click="close"><i class="pop-header__close"></i></button>
        <div class="pop-header__content">
          <div class="pop-header__icon">🧡️️️</div>
          <div class="pop-header__text">
            <div class="pop-header__title">낭만성향분석</div>
          </div>
        </div>
      </div>
      <div class="pop-body intro" :class="isMobile ? 'pd-16' : 'pd-40'">
        <div class="pop-body__inner">
          <div class="img-nangmanee-box">
            <div class="img-nangmanee"></div>
          </div>
          <div class="sub-title-txt-box">나를 이해하기 위한 출발점!</div>

          <div class="pop-body__content st-disposition-intro">
            <div class="speech-bubble__container">
              <div class="speech-bubble__row orange">
                <div class="speech-bubble">
                  나는 왜 이런 성향을 가졌을까?
                  <div class="speech-bubble__tail"></div>
                </div>
              </div>
              <div class="speech-bubble__row white">
                <div class="speech-bubble">
                  타고난 기질?
                  <div class="speech-bubble__tail"></div>
                </div>
              </div>
              <div class="speech-bubble__row white">
                <div class="speech-bubble">
                  아니면 살아오면서 형성된 모습?
                  <div class="speech-bubble__tail"></div>
                </div>
              </div>
            </div>
            <div class="speech-bubble__desc">
              우리는 가끔 스스로에게 질문하게 됩니다.<br />
              명확히 구분할 수는 않지만 <span class="highlight">‘나를 더 잘 알고 싶다’</span>는
              마음만큼은 분명합니다.<br />
              낭만성향분석은 그런 마음에서 출발했습니다.
            </div>
          </div>
        </div>

        <button
          style="margin-top: 40px"
          :style="{ marginBottom: `calc(16px + var(--safe-area-bottom, 0px))` }"
          class="start-button"
          @click="openInfoPage"
        >
          바로 시작하기
        </button>
      </div>
    </div>

    <div v-if="currentPage !== 0 && readyToShow" class="pop-saju-reading-inner gray100">
      <div class="pop-header__small">
        <button @click="close"><i class="pop-header__close"></i></button>
        <div class="pop-header__content">
          <div class="pop-header__title_2 st-3">
            <span class="tit">낭만성향분석</span>
            <span class="sub-tit">나를 이해하기 위한 출발점</span>
          </div>
        </div>
      </div>

      <div v-if="currentPage === 1" class="pop-body mobile st-content">
        <div class="overview-pay-info-box">
          <div class="tit-box"><IconAnalysNav210 /> <span>낭만성향분석은?</span></div>
          <div class="txt-box">
            <div class="txt">
              <span class="txt-bold">사주명리학의 통찰, 인공지능의 분석력,</span> 그리고
              <span class="txt-bold">심리학적 성향 해석 방식</span>을 결합하여 현실적인 성향 분석
              도구로 개발되었습니다.
            </div>
            <div class="txt">
              사주원국의 오행구조와 격국, 용신 분석을 바탕으로
              <span class="txt-bold">타고난 기질의 중심 성향(기본성향코드)</span>과
              <span class="txt-bold">삶의 균형을</span>
              <span class="txt-bold">돕는 보완 에너지(용신코드)</span>를 함께 제시함으로써 보다
              입체적이고 실제적인 자기 이해를 돕고자 합니다.
            </div>
          </div>
        </div>
        <section class="guide-box">
          <div class="guide-box__title" :class="{ mobile: isMobile }">
            <div class="box-title__keyword">분석의 특징</div>
            <span class="box-title__txt"> 응답이 아닌 구조로 성향을 해석합니다!</span>
          </div>
          <div class="box-content gray">
            <div class="cont-top_txt">
              낭만성향분석은 사람마다 다른 감정 상태나 환경의 영향을 최소화하기 위해,<br />
              생년월일을 기반으로 구성된
              <span class="highlight">사주원국의 오행 구조를 분석</span>합니다.
            </div>
            <div class="cont-bottom">
              <div class="guide-eight-img" />
            </div>
          </div>
          <div class="box-body">
            <span class="box-body__txt">
              문항이 아닌 <span class="highlight">구조</span>, 응답이 아닌
              <span class="highlight">원리</span>에 <span class="highlight">기반한 해석</span>을
              통해 더 균형 있고 깊이 있는 성향 분석을 제공해드리고자 합니다.
            </span>
          </div>
        </section>
      </div>

      <div v-if="currentPage === 2" class="pop-body mobile">
        <div class="pop-body__inner page-content-box">
          <div class="personality-wrapper">
            <section class="guide-box">
              <div class="guide-box__title" :class="{ mobile: isMobile }">
                <div class="box-title__keyword">분석 형식</div>
                <span class="box-title__txt">
                  익숙한 형식 안에 명리학적 해석 기준을 담았습니다.
                </span>
              </div>
              <div class="box-body">
                <span class="box-body__txt">
                  요즘은 다양한 온라인 심리 검사나 콘텐츠를 통해 자신의 성향을 유형화해보는 것이
                  자연스러운 경험이 되었습니다.
                </span>
                <span class="box-body__txt">
                  <span class="highlight">4자리 코드 형식(ex ESFJ 등)</span>은 <br />기질을 간편하고
                  직관적으로 이해할 수 있는 방식으로 널리 활용되고 있습니다.
                </span>
              </div>
              <div class="box-content gray">
                <span class="cont-top_txt">
                  낭만성향분석은 이러한 익숙한 형식을 참고하여<br />사용자가 보다 편하게 자신의
                  성향을 이해할 수 있도록 <br /><span class="highlight">결과를 4자리 코드 형식</span
                  >을 참고하여 분석 결과를 전달해 드리고 있습니다.
                </span>
                <div class="guide-star-grape-box">
                  <div class="guide-star-grape-img-box"></div>
                </div>
                <div class="guide-balance-code-box" :class="{ mobile: isMobile }">
                  <div class="txt-box">성향조화를 돕는 밸런스 코드</div>
                  <div class="code-box">
                    <span class="exp-txt">예시</span>
                    <span class="code-txt-box">
                      <span class="code-txt color1">E</span>
                      <span class="code-txt color2">S</span>
                      <span class="code-txt color3">F</span>
                      <span class="code-txt color4">J</span>
                    </span>
                    <span class="icon-plus"></span>
                    <span class="code-txt-black">B</span>
                  </div>
                </div>
              </div>
            </section>
            <section class="guide-box">
              <div class="box-body">
                <span class="box-body__txt">
                  그러나 익숙한 표현방식으로 제공해드리지만 그 안에 담긴
                  <span class="highlight">분석의 기준과 해석의 방식</span>은 본질적으로 다릅니다.
                </span>
                <span class="box-body__txt">
                  <span class="highlight">사주명리학의 해석 원리</span>를 기반으로<br />
                  타고난 오행의 배치와 조후(調候), 그리고 오행 간의 균형과 흐름까지 종합적으로
                  읽어내어
                  <span class="highlight">핵심 기질과 경향적 성향 패턴을 도출</span>하고 있습니다.
                </span>
              </div>
              <!-- <div class="box-img">
              <div class="guide-star-img" />
            </div> -->
            </section>
          </div>
        </div>
      </div>
      <div v-if="currentPage === 3" class="pop-body mobile">
        <div class="pop-body__inner page-content-box">
          <div class="personality-wrapper">
            <section class="guide-box">
              <div class="guide-box__title" :class="{ mobile: isMobile }">
                <div class="box-title__keyword">분석의 확장</div>
                <span class="box-title__txt">진로와 반려친구, 일상의 조화까지!</span>
              </div>

              <div class="box-content gray">
                <div class="cont-top_txt">
                  낭만성향분석은
                  <span class="highlight"
                    >사주원국의 구조, 오행의 생극제화, 격국과 용신 분석을 바탕</span
                  >으로<br />
                  실생활에 연결된 제안을 함께 드리고 있습니다.
                </div>
                <div class="box-img">
                  <div class="guide-star-img" />
                </div>
              </div>

              <div class="box-content border-box">
                <div class="icon-course"></div>
                <div class="border-box__content">
                  <div class="border-box__title">진로 추천</div>
                  <div>
                    격국과 용신의 특성, 기운을 참고하여 성향에 어울릴 수 있는 진로방향을 탐색해보실
                    수 있도록
                    <span class="highlight">우선 추천</span>과
                    <span class="highlight">보조 추천</span>으로 나누어 안내해 드립니다.
                  </div>
                </div>
              </div>

              <div class="box-content border-box">
                <div class="icon-pet"></div>
                <div class="border-box__content">
                  <div class="border-box__title">반려친구 추천</div>
                  <div>
                    성향 분석 결과와 용신·희신의 흐름을 고려하여, 자연과의 어울림을 고민하는 분들께
                    반려동물과 반려식물 친구를 안내해드립니다.
                  </div>
                  <div class="border-box__description">
                    ※ 이미 반려생활 중인 분들께도 또 다른 관점에서 자신을 이해해보는 하나의 참고
                    자료로 활용해보시길 권합니다.
                  </div>
                </div>
              </div>

              <div class="box-body">
                <div class="box-body__txt">
                  낭만성향분석은 이와 같이 명리학의 핵심 원리를 바탕으로 일상의 다양한 순간을
                  탐색해보는 길을 따뜻하게 열어가고자 합니다.
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      <div v-if="currentPage === 4" class="pop-body mobile">
        <div class="pop-body__inner page-content-box">
          <div class="personality-wrapper">
            <section class="guide-box">
              <div class="guide-box__title" :class="{ mobile: isMobile }">
                <div class="box-title__keyword">성향 분석의 활용</div>
                <span class="box-title__txt">나를 이해하고 조율하는 이정표로!</span>
              </div>

              <div class="box-content gray">
                <div class="cont-top_txt">
                  낭만성향분석은 타고난 기질과 시기에 따라 달라질 수 있는 성향을 함께
                  해석함으로써,<br />보다 현실적이고 유연한 자기이해의 방향을 제안합니다.
                </div>
              </div>

              <div class="box-body">
                <span class="box-body__txt">
                  중심 성향뿐 아니라, 유사 성향과 시기별 변화 양상까지 열린 시선으로 함께
                  살펴본다면,<br />나에 대한 이해는 한층 깊어지고 유연해질 수 있습니다.
                </span>
                <span class="box-body__txt">
                  또한 성향은 시기적 에너지에 따라 강조점이 달라질 수 있기 때문에,
                  <span class="highlight">새로운 흐름이 시작되는 시점인 신년이나 대운변화 시점</span
                  >에 한 번쯤 다시 살펴보는 것도 좋은 자기 점검이 될 수 있습니다.
                </span>
              </div>

              <div class="box-content border-box sm">
                <div class="icon-speaker"></div>
                <div class="border-box__content">
                  시간의 흐름에 따라 변화하는 에너지(대운, 세운 등)를 반영한 추가 성향 분석 서비스도
                  곧 제공해드릴 예정입니다!
                </div>
              </div>

              <div class="guide-quote">
                <div class="icon-quote"></div>
                <div>
                  분석 결과가
                  <span class="txt-bold"
                    >‘나를 규정짓는 단 하나의 정의’가 아니라
                    <div class="text-underline"></div></span
                  ><br />
                  이미 내 안에 존재하지만<br />
                  <span class="txt-bold highlight"
                    >아직 조명되지 않았던 가능성을 더 깊이 이해할 수 있는
                    <div class="text-underline"></div></span
                  ><br />
                  작은 빛으로 다가가기를 바랍니다.
                </div>
              </div>
              <div class="guide-outro bg-highlight">
                <div class="img-nangmanee"></div>
                <div class="guide-outro__content">
                  <div class="guide-outro__title">낭만만세력은</div>
                  <div class="guide-outro__text">
                    이 성향분석이 삶의 방향을 조율해가는<br />
                    따뜻한 이정표가 될 수 있도록 지속적으로 연구하고,<br />
                    더 나은 서비스로 발전시켜 나가겠습니다.
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      <div v-if="currentPage === 5" class="pop-body mobile mobile-content-first">
        <div class="pop-body__inner page-content-box">
          <SummarySub01
            :baseCode="baseCode"
            :pData="sData"
            :pSubData="pSubData"
            :userName="prSajuData?.inputdata?.name"
            :guestUserId="guestUserId"
          />
        </div>
      </div>
      <div v-if="currentPage === 6" class="pop-body mobile mobile-content-second">
        <div class="pop-body__inner page-content-box">
          <SummarySub02
            :baseCode="baseCode"
            :pData="sData"
            :pSubData="pSubData"
            :userName="prSajuData?.inputdata?.name"
            :guestUserId="guestUserId"
          />
        </div>
      </div>
      <div v-if="currentPage === 7" class="pop-body mobile">
        <div class="pop-body__inner page-content-box">
          <div
            class="title-saju-reading-finish-box"
            :style="{ paddingBottom: isMobile ? '52px' : '0px' }"
          >
            <div class="txt-box">
              <div class="txt">
                <span class="icon">🧡</span>
                <div class="saju-pop__outro">
                  성향을 이해한다는 건<br />
                  내 안의 가능성을 펼쳐갈 수 있는<br />
                  작은 방향을 찾는 일이기도 해요.
                  <br /><br />
                  숨겨진 나를 마주하는 특별한 시간~
                  <br /><br />
                  <b>로그인 -> 사주저장 후, </b><br />
                  <b><span class="colored-txt">낭만분석</span>에서 확인해보세요.</b>
                </div>
              </div>
            </div>

            <div class="pop-disposition-bottom">
              <button class="bg-button" @click="routeTab">낭만분석 바로 가기</button>
              <button class="bg-button white" @click="goToPurchasePage">
                낭만성향분석 구매하러 가기
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="pop-pagination" v-if="!infoPage && currentPage !== 0">
        <div class="pop-pagination__index">0{{ currentPage }} / 07</div>
        <button class="pop-pagination__prev" @click="clickPrevPage">
          <i class="icon-chevron"></i>
        </button>
        <button class="pop-pagination__next" @click="clickNextPage">
          <i class="icon-chevron right"></i>
        </button>
      </div>
    </div>
  </div>
  <ConfirmComponent
    ref="dialogConfirm"
    :title="dialogTitle"
    :title2="dialogTitle2"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
  />
</template>
<style lang="scss">
@import '@/assets/nangman_style/mobile/saju-reading-common.scss';
@import '@/assets/nangman_style/_analysis_main_color.scss';
.pop-saju-reading-container.st-mobile-app .pop-saju-reading-inner .pop-header__close {
  top: calc(15px + var(--safe-area-top, 0px));
}
.saju-pop-common-dialog
  .pop-saju-reading-container
  .pop-saju-reading-inner
  .pop-body__inner.page-content-box {
  padding: 24px 40px !important;
}

.saju-pop-common-dialog {
  height: 720px;
}
.pop-saju-reading-container .pop-saju-reading-inner.intro.st-disposition {
  background: #fef8f6;
}
.pop-saju-reading-container .pop-saju-reading-inner.st-disposition .pop-header__large.intro {
  background: #fef8f6;
}
.pop-saju-reading-container .pop-saju-reading-inner.intro.st-disposition .pop-body {
  background: #fef8f6;
}
.pop-saju-reading-container
  .pop-saju-reading-inner
  .pop-body
  .pop-body__content.st-disposition-intro {
  padding: 0;
  gap: 32px;
}
.pop-saju-reading-container .pop-saju-reading-inner .pop-body .sub-title-txt-box {
  margin-bottom: 16px;
}

.st-mobile-app {
  .pop-header__title_2.st-3 {
    background: var(--Orange-100, #fff8f5);
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    .tit {
      color: var(--Gray-800, #333);
      text-align: center;
      font-family: Pretendard;
      font-size: 24px;
      font-style: normal;
      font-weight: 700;
      line-height: 32px; /* 133.333% */
    }
    .sub-tit {
      color: var(--Gray-700, #575757);
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 500;
      line-height: 24px; /* 160% */
    }
  }
}
.pop-saju-reading-container .pop-saju-reading-inner .pop-body.st-content {
  display: flex;
  width: 100%;
  padding: 24px 40px;
  flex-direction: column;
  align-items: center;
  gap: 40px;
}

.saju-pop-common-dialog.pc-saju-dispoition-wrap
  .pop-saju-reading-container
  .pop-saju-reading-inner
  .pop-body__inner.page-content-box {
  padding: 24px 40px !important;
}
.pc-saju-dispoition-wrap,
.st-mobile-app {
  .overview-pay-info-box {
    display: flex;
    padding: 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
    border-radius: 12px;
    background: $analysis-personality-light;
    .tit-box {
      color: $analysis-personality;
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px; /* 160% */
    }
    .txt-box {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      align-self: stretch;
      .txt {
        color: var(--Gray-700, #575757);
        font-family: Pretendard;
        font-size: 15px;
        font-style: normal;
        font-weight: 500;
        line-height: 24px;
        .txt-bold {
          color: var(--Gray-700, #575757);
          font-family: Pretendard;
          font-size: 15px;
          font-style: normal;
          font-weight: 700;
          line-height: 24px; /* 160% */
        }
      }
    }
  }
  .guide-eight-img {
    width: 270px;
    aspect-ratio: 270/116;
    background: url('@manseImages/personality/personality-guide-eight.png') center center no-repeat;
    background-size: contain;
  }

  .guide-star-grape-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }
  .guide-star-grape-img-box {
    width: 332px;
    aspect-ratio: 332/200;
    background: url('@manseImages/personality/guide-star-grape.png') center center no-repeat;
    background-size: contain;
  }
  .guide-balance-code-box {
    display: flex;
    padding: 8px 16px;
    justify-content: center;
    align-items: center;
    gap: 20px;
    border-radius: 12px;
    border: 1px solid var(--Gray-300, #ebebeb);
    background: var(--gray-100-white, #fff);
    &.mobile {
      flex-direction: column;
      padding: 8px 20px;
      gap: 8px;
    }
    .txt-box {
      color: var(--Gray-800, #333);
      text-align: center;
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px; /* 160% */
    }
    .code-box {
      display: flex;
      align-items: center;
      gap: 8px;
      .exp-txt {
        display: flex;
        height: 22px;
        padding: 2px 6px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        border-radius: 30px;
        background: var(--Gray-300, #ebebeb);
        color: var(--Gray-500, #949494);
        font-family: Pretendard;
        font-size: 11px;
        font-style: normal;
        font-weight: 700;
        line-height: 16px; /* 145.455% */
      }
      .code-txt-box {
        display: flex;
        align-items: center;
        gap: 4px;
        opacity: 0.2;
        .code-txt {
          color: #ff6867;
          text-align: center;
          font-family: Pretendard;
          font-size: 28px;
          font-style: normal;
          font-weight: 700;
          line-height: 32px; /* 114.286% */
          &.color1 {
            color: #ff6867;
          }
          &.color2 {
            color: #7d67fd;
          }
          &.color3 {
            color: #e53c9c;
          }
          &.color4 {
            color: #4881ce;
          }
        }
      }
      .icon-plus {
        width: 20px;
        height: 20px;
        background: url('@manseImages/personality/icon_plus2.png') center center no-repeat;
        background-size: contain;
      }
      .code-txt-black {
        color: var(--gray-100-white, #fff);
        text-align: center;
        font-family: Pretendard;
        font-size: 18px;
        font-style: normal;
        font-weight: 700;
        line-height: 32px; /* 177.778% */
        display: flex;
        width: 32px;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 10px;
        border-radius: 40px;
        background: var(--black-100, #404040);
      }
    }
  }

  .guide-star-img {
    height: 160px;
    aspect-ratio: 9/8;
    background: url('@manseImages/personality/prsonaity-guidle-star.png') center center no-repeat;
    background-size: contain;
  }
  .guide-eight-img {
    width: 270px;
    aspect-ratio: 270/116;
    background: url('@manseImages/personality/personality-guide-eight.png') center center no-repeat;
    background-size: contain;
  }
  .icon-course {
    width: 60px;
    height: 60px;
    background: url('@manseImages/personality/icon-recommend_course.svg') center center no-repeat;
    background-size: contain;
  }
  .icon-pet {
    width: 60px;
    height: 60px;
    background: url('@manseImages/personality/icon-recommend_pet.svg') center center no-repeat;
    background-size: contain;
  }
  .icon-speaker {
    width: 18px;
    height: 18px;
    background: url('@manseImages/personality/icon-speaker_orange.svg') center center no-repeat;
    background-size: contain;
  }
  .icon-quote {
    width: 28px;
    height: 22px;
    background: url('/src/assets/images_manse/icon_quotes.svg') center center no-repeat;
    background-size: contain;
  }

  .guide-quote {
    padding: 20px 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    color: var(--Gray-800, #333);
    text-align: center;
    font-family: Pretendard;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 28px;
    .txt-bold {
      position: relative;
      font-weight: 700;
      .text-underline {
        position: absolute;
        right: 0;
        bottom: -2px;
        width: 100%;
        height: 8px;
        opacity: 0.15;
        background: var(--Gray-800, #333);
        z-index: -1;
        border-radius: 0;
      }
    }
    .txt-bold.highlight {
      .text-underline {
        background: #f47a49;
      }
    }
  }

  .guide-outro {
    padding: 20px 32px;
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: 20px;
    .guide-outro__content {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 8px;
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      line-height: 24px;
    }
    .guide-outro__title {
      color: $analysis-personality;
      font-weight: 700;
    }
    .guide-outro__text {
      color: var(--Gray-800, #333);
      font-weight: 400;
    }
  }
  .img-nangmanee-box {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    margin-bottom: 20px;
  }
  .img-nangmanee {
    width: 132px;
    height: 132px;
    background: url('@manseImages/personality/img-nangmanee.png') no-repeat;
    background-size: contain;
  }
  .speech-bubble__desc {
    width: 100%;
    display: block;
    text-align: center;
    color: var(--Gray-800, #333);
    text-align: center;
    font-family: Pretendard;
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px; /* 160% */
    .highlight {
      color: #f47a49;
      font-weight: 500;
    }
  }
  .speech-bubble__container {
    display: flex;
    padding: 12px 32px;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    align-self: stretch;
    border-radius: 12px;
    background: var(--gray-100-white, #fff);
    .speech-bubble__row {
      width: 100%;
      display: flex;
      .speech-bubble {
        position: relative;
        width: fit-content;
        padding: 4px 12px;
        border-radius: 12px;
        font-family: Pretendard;
        font-size: 15px;
        font-style: normal;
        font-weight: 700;
        line-height: 24px;
      }
      .speech-bubble__tail {
        position: absolute;
        top: 50%;
        width: 28px;
        height: 28px;
      }
      &.orange {
        justify-content: flex-start;
        .speech-bubble {
          background: #f47a49;
          text-align: left;
          color: var(--gray-100-white, #fff);
        }
        .speech-bubble__tail {
          left: 0;
          transform: translate(-50%, -50%);
          background: url('/src/assets/images_manse/personality/speech-bubble__tail-orange.svg')
            center center no-repeat;
        }
      }
      &.white {
        justify-content: flex-end;
        .speech-bubble {
          border-radius: 12px;
          background: var(--gray-100-white, #fff);
          text-align: right;
          color: var(--Gray-700, #575757);
          box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
        }
        .speech-bubble__tail {
          right: 0;
          transform: translate(50%, -50%);
          background: url('/src/assets/images_manse/personality/speech-bubble__tail-white.svg')
            center center no-repeat;
        }
      }
    }
  }
  .personality-wrapper {
    padding: 0 !important;
  }
  .pop-header__title_2.st-3 {
    padding: 16px 24px;
    height: 60px;
    gap: 6px;
    flex-direction: row;
  }
  .pop-saju-reading-container .pop-saju-reading-inner .pop-header__close {
    top: calc(18px + var(--safe-area-top, 0px));
  }

  .pop-saju-reading-container .pop-saju-reading-inner .pop-body.st-content {
    padding-top: 16px;
    padding-bottom: 16px;
  }
  .guide-quote {
    padding: 8px 0;
    gap: 8px;
    line-height: 24px;
  }
  .guide-box .box-content.border-box {
    padding: 16px 24px;
  }
  .pop-saju-reading-container
    .pop-saju-reading-inner
    .pop-body
    .pop-body__content.st-disposition-intro {
    gap: 32px;
  }
  .speech-bubble__container {
    padding: 24px;
  }
  .saju-pop-common-dialog
    .pop-saju-reading-container
    .pop-saju-reading-inner
    .pop-body__inner.page-content-box {
    padding: 16px 40px;
  }
  .saju-pop-common-dialog
    .pop-saju-reading-container
    .pop-saju-reading-inner
    .pop-body__inner.page-content-box {
    padding: 16px 24px;
  }
}
/* 불필요시 삭제 할 것 */
.title-saju-reading-finish-box {
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  gap: 40px;
  .txt-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .txt {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    color: var(--Gray-700, #575757);
    text-align: center;
    font-family: Pretendard;
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px; /* 160% */
    .icon {
      color: var(--Gray-800, #333);
      font-family: Pretendard;
      font-size: 28px;
      font-style: normal;
      font-weight: 700;
      line-height: 28px; /* 100% */
    }
  }
}
.pop-body__title {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px !important;
  .icon {
    font-size: 28px !important;
    font-weight: 700;
    display: flex;
    width: 44px !important;
    height: 44px;
    align-items: center;
  }
  .title {
    display: flex;
  }
}
.pop-body__desc-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  .tit {
    color: var(--Gray-700, #575757);
    font-family: Pretendard;
    font-size: 15px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px; /* 160% */
  }
  .txt_wrap {
    width: 100%;
    .txt {
      color: var(--Gray-700, #575757);
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px; /* 160% */
    }
  }
}
.pop-body__tit {
  display: flex;
  padding: 12px 16px;
  align-items: center;
  gap: 12px;

  border-radius: 8px;

  font-family: Pretendard;
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;

  &.five01 {
    background: rgba(194, 53, 53, 0.05);
  }
  &.five02 {
    background: rgba(64, 64, 64, 0.05);
  }
  &.five03 {
    background: rgba(94, 159, 28, 0.05);
  }
  &.five04 {
    background: rgba(148, 148, 148, 0.05);
  }
  &.five05 {
    background: rgba(219, 152, 35, 0.05);
  }
}
.bg-button {
  display: flex;
  width: 100%;
  height: 48px;
  min-width: 80px;
  padding: 14px 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  background: var(--orange-300-main, #eb4c10);
  color: var(--gray-100-white, #fff);
  text-align: center;
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  &.white {
    border: 1px solid var(--orange-300-main, #eb4c10);
    background: var(--gray-100-white, #fff);
    color: var(--orange-300-main, #eb4c10);
  }
}

// .saju-reading-dialog .pop-saju-reading-container .pop-saju-reading-inner {
//   position: relative;
// }
// .saju-reading-dialog .pop-saju-reading-container .pop-saju-reading-inner .pop-body {
//   .pop-body__content {
//     gap: 0;
//     border-radius: 12px;
//   }
//   .pop-body__desc {
//     border-radius: 0;
//   }
// }
.default-inner-box {
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
}

.saju-pop__outro {
  color: var(--Gray-800, #333);
  text-align: center;
  font-family: Pretendard;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  .colored-txt {
    color: var(--orange-300-main, #eb4c10);
  }
}
.pop-disposition-bottom {
  width: 343px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}
.pop-saju-reading-container.st-mobile-app {
  .pop-saju-reading-inner .pop-body.st-content {
    height: calc(100vh - 142px) !important;
    padding: 24px !important;
  }
  .pop-saju-reading-inner .pop-body {
    height: calc(100vh - 142px) !important;
    overflow-y: auto;
  }
  .personality-wrapper {
    padding: 0 !important;
  }
  // 5/6페이지 성향 분석 카드: 부모가 스크롤하도록 내부 카드의 고정 높이/내부 스크롤 해제
  .pop-body.mobile-content-first,
  .pop-body.mobile-content-second {
    // 외부 래퍼의 PC용 40px 좌우 패딩 제거 (카드가 전체 너비 사용)
    .pop-body__inner.page-content-box {
      padding: 0 !important;
    }
    .v-row.full-height {
      height: auto !important;
      margin: 0 !important;
    }
    .no-pad-col {
      height: auto !important;
    }
    .personality-card.full-height {
      height: auto !important;
      overflow-y: visible !important;
    }
    // global `.personality-wrapper { padding: 0 !important }`을 복원하여 카드 내부 여백 확보
    .personality-wrapper {
      padding: 16px !important;
    }
    .personality-wrapper.with-tit {
      padding: 16px !important;
      gap: 24px;
    }
  }
}
</style>
